import React, { useState } from "react";
import { Button, TextField, Paper } from "@mui/material";
import axios from "axios";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import logo from "../assets/valonix-logo-siyah.png";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { API_URL } from "../config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useTranslation } from "react-i18next";

const NAVY = '#1a365d';
const NAVY_HOVER = '#2c5282';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="#" underline="hover">
        VALONIX
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: NAVY,
      dark: NAVY_HOVER,
    },
    secondary: {
      main: NAVY_HOVER,
    },
  },
});

const Forgot = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleCheckUser = async () => {
    const normalizedEmail = (email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error(t("auth.forgot.emailRequired"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      toast.error(t("auth.forgot.emailInvalid"));
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API_URL}/auth/request-password-reset`,
        { email: normalizedEmail },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("Response:", response.data);
      toast.success(t("auth.forgot.sent"));
    } catch (error) {
      console.error("Hata:", error);

      const detail =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        t("auth.forgot.error");

      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: NAVY,
          padding: 2,
        }}
      >
        <CssBaseline />
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={logo}
              alt={t("auth.logoAlt")}
              sx={{
                width: '60%',
                maxWidth: '250px',
                height: 'auto',
                marginBottom: 2,
              }}
            />

            {/* Icon & Title */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: NAVY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <LockResetIcon sx={{ fontSize: 32, color: '#fff' }} />
            </Box>

            <Typography
              component="h1"
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: 1,
                textAlign: 'center',
              }}
            >
              {t("auth.forgot.title")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                marginBottom: 3,
                textAlign: 'center',
                px: 2,
              }}
            >
              {t("auth.forgot.intro")}
            </Typography>

            {/* Form */}
            <Box noValidate sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t("auth.forgot.email")}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCheckUser();
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(15, 39, 68, 0.35)',
                  backgroundColor: NAVY,
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(15, 39, 68, 0.45)',
                    backgroundColor: NAVY_HOVER,
                  },
                }}
                onClick={handleCheckUser}
                disabled={submitting}
              >
                {submitting ? t("auth.forgot.sending") : t("auth.forgot.submit")}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(15, 39, 68, 0.04)',
                  },
                }}
                onClick={() => navigate('/')}
              >
                {t("auth.forgot.back")}
              </Button>
            </Box>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Forgot;